/* eslint-disable camelcase */
import React, {
	useMemo,
	useEffect,
	useState,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { CUSTOM_DESTINATION_OPTIONS, DESTINATION_TYPE, RESOLUTION_OPTIONS } from '../../stores/reducers/broadcasting';
import { SelectGroupOption } from './select-group';
import { GreenroomState as GS } from '../../stores/types/types';
import Socket from '../../connection/socket';
import * as SettingsActions from '../../stores/actions/settings';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import Icon, { ICONS, COLORS } from './icon';
import Switch from './switch';
import SelectSmall from './select-small';
import * as Alerts from './alert-service';
import { isValidRtmpAddress } from '../../utils/utils';
import { random } from '../../utils/random';
import { APP } from '../../stores/session-status';
import { CustomDestination } from '../../stores/reducers/settings';
import { StreamSettingLocation } from '../right-panel/settings/streaming-settings';
import { Meeting } from '../../stores/reducers/session';
import LoadingSpinner from './loading-spinner';
import useValidateRtmp from '../../hooks/use-validate-rtmp';

interface Props {
	id: string;
	name: string;
	url: string;
	stream_key: string;
	resolution: string;
	logo: string;
	type: number;
	destinationType: SelectGroupOption;
	socket: Socket | null;
	editingDestination: boolean;
	selected: string;
	meeting: Meeting;
	goLiveButtonClicked: boolean;
	handleEditDestination: Function;
	toggleOn: Function;
	location: string;
}

const CARD_HEIGHTS = {
	HIDE: 0,
	CLOSE: 72,
	OPEN: 230,
};

export default function DestinationCard(props: Props) {
	const dispatch = useDispatch();
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const session = useSelector((s: GS) => s.SessionReducer.session);
	const defaultResolution = useSelector((s: GS) => s.SettingsReducer.defaultResolution);
	const useDefaultEndpoint = useSelector((s: GS) => s.SettingsReducer.useDefaultEndpoint);
	const useCustomEndpoint = useSelector((s: GS) => s.BroadcastReducer.useCustomEndpoint);
	const appSignedInWith = useSelector((s: GS) => s.SessionReducer.appSignedInWith);

	const {
		id,
		name,
		url,
		stream_key,
		resolution,
		logo,
		type,
		socket,
		destinationType,
		editingDestination,
		selected,
		meeting,
		goLiveButtonClicked,
		handleEditDestination,
		toggleOn,
		location,
	} = props;

	const [editName, setEditName] = useState<string>('');
	const [editAddress, setEditAddress] = useState<string>('');
	const [editKey, setEditKey] = useState<string>('');
	const [editResolution, setEditResolution] = useState<string>('');
	const [cardHeight, setCardHeight] = useState<number>(id ? CARD_HEIGHTS.CLOSE : CARD_HEIGHTS.HIDE);
	const [newDestination, setNewDestination] = useState<CustomDestination | null>(null);

	const cardRef = useRef<HTMLDivElement>(null);

	const {
		isValidatingRtmpEndpoint,
		rtmpOverrideModal,
		validateRtmpEndpoint,
	} = useValidateRtmp();

	const openDestinationCard = useMemo(
		() => editingDestination && !newDestination,
		[editingDestination, newDestination],
	);

	const isNewDestination = useMemo(
		() => !!CUSTOM_DESTINATION_OPTIONS.find((opt) => id === opt.label),
		[id],
	);

	const isDefaultDestination = useMemo(() => id === 'Default', [id]);

	function resetFields(closeCard: boolean = true) {
		setEditName(name);
		setEditAddress(url);
		setEditKey(stream_key);
		setEditResolution(resolution);
		if (closeCard) {
			handleEditDestination('');
		}
	}

	function cancelClick() {
		if (isNewDestination) {
			// when canceling a new destination
			setCardHeight(CARD_HEIGHTS.HIDE);
		} else {
			// when canceling an existing destination
			resetFields();
		}
	}

	function handleClick(e: any) {
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (path[i].className?.includes) {
				if ((typeof path[i].className === 'string'
					&& (path[i].className.includes('destination-card edit')
					|| path[i].className.includes('destination-card-edit')
					|| path[i].className.includes('destination-options'))
				)) {
					keepOpen = true;
					break;
				}
			}
		}

		if (!keepOpen) {
			cancelClick();
		}
	}

	function showError(message: string) {
		Alerts.showAlert({
			message,
			type: 'warning',
			duration: 3000,
		});
	}

	function saveDefaultResolution() {
		if (useDefaultEndpoint) {
			dispatch(SettingsActions.setSelectedBroadcastResolution(editResolution));
		}
		// Needs to be called after setting broadcast resolution
		// as this triggers a reload from the database and overwrites otherwise.
		dispatch(SettingsActions.setDefaultResolution(editResolution));
		resetFields();
	}

	function saveDestination() {
		const updateInputAddress = editAddress.replace(/\s+/g, '');

		setEditAddress(updateInputAddress);

		const editDestination: CustomDestination = {
			id,
			name: editName,
			url: updateInputAddress,
			stream_key: editKey,
			resolution: editResolution,
			logo: '',
			type,
		};

		if (isNewDestination) {
			setNewDestination(editDestination);
		} else {
			// Need to do this before updating custom destination
			// Custom destination update will trigger a reload of settings
			// from the database.  When fired first, it grabs what is currently in db
			// with outdated broadcast resolution.  The following sets the resolution
			// then the payload from updateCustomDestination overwrites that with the
			// old value.
			// Updated for https://brandlive.atlassian.net/browse/GREEN-4079
			if (selected === id) {
				// if this custom destination is currently toggled on,
				// update the custom broadcast endpoint and selected resolution
				dispatch(BroadcastActions.updateCustomBroadcastEndpoint(
					token!,
					meeting.meeting,
					editName,
					updateInputAddress,
					editKey,
				));
				dispatch(SettingsActions.setSelectedBroadcastResolution(editResolution));
				socket?.sendMessage(
					'custom-endpoint-on-sync',
					{
						name: editName,
						url: updateInputAddress,
						stream_key: editKey,
						from: session,
					},
				);
			}
			dispatch(SettingsActions.updateCustomDestination(editDestination));
			resetFields();
		}
	}

	function removeDestination() {
		if (!token || !meeting) return;

		if (useCustomEndpoint && selected === id) {
			dispatch(BroadcastActions.updateUseCustomBroadcastEndpoint(token, meeting.meeting, false));
			dispatch(SettingsActions.setSelectedCustomDestination(''));
			socket?.sendMessage('custom-endpoint-off-sync', { from: session });

			if (appSignedInWith === APP.EVENTS) {
				dispatch(SettingsActions.setUseDefaultEndpoint(true));
			}

			const res = appSignedInWith === APP.EVENTS ? defaultResolution : '1080p';
			dispatch(SettingsActions.setSelectedBroadcastResolution(res));
		}

		if (id) {
			dispatch(SettingsActions.removeCustomDestination(id));
		}

		resetFields();
	}

	function getScrollContainer(containerLocation: string) {
		switch (containerLocation) {
			case StreamSettingLocation.MetaMenu:
				return document.getElementById('meta-event-settings');
			case StreamSettingLocation.StreamModal:
				return cardRef.current?.parentElement ?? null;
			default:
				return null;
		}
	}

	const logoImage = useMemo(() => {
		if (type !== DESTINATION_TYPE.OTHER) return destinationType.description;
		return logo || destinationType.description;
	}, [logo, type]);

	useEffect(() => {
		resetFields(false);
	}, [name, url, stream_key, resolution]);

	useEffect(() => {
		if (editingDestination || isNewDestination) {
			window.addEventListener('click', handleClick);
		}

		return () => {
			window.removeEventListener('click', handleClick);
		};
	}, [editingDestination, isNewDestination]);

	useEffect(() => {
		if (openDestinationCard) {
			setCardHeight(CARD_HEIGHTS.OPEN);
		} else {
			setCardHeight(CARD_HEIGHTS.CLOSE);
		}
	}, [openDestinationCard]);

	useEffect(() => {
		if (!editingDestination && isNewDestination) {
			setCardHeight(CARD_HEIGHTS.HIDE);
		}
	}, [editingDestination, isNewDestination]);

	return (
		<div
			className={classNames('destination-card', { edit: openDestinationCard, disabled: goLiveButtonClicked })}
			data-testid="destination-card"
			id={id}
			style={{
				height: `${cardHeight}px`,
				padding: cardHeight ? '16px 24px' : '0px 24px',
				marginBottom: cardHeight ? '16px' : '0px',
			}}
			onTransitionEnd={({ propertyName }) => {
				if (propertyName !== 'height') return;

				// remove destination animation has completed and it can be removed from the db
				if ((openDestinationCard || !editingDestination) && cardHeight === CARD_HEIGHTS.HIDE) {
					removeDestination();
				}

				// save destination animation has completed and it can be added to the db
				if (!openDestinationCard && newDestination) {
					const newId = random(10);
					dispatch(SettingsActions.addCustomDestination({ ...newDestination, id: newId }));
					resetFields();
				}

				// scroll to card if it is open
				if (openDestinationCard && cardHeight === CARD_HEIGHTS.OPEN) {
					const scrollContainer = getScrollContainer(location);
					if (cardRef.current && scrollContainer) {
						const offset = location === StreamSettingLocation.MetaMenu ? 72 : 0;

						const containerTop = scrollContainer.scrollTop;
						const containerBottom = containerTop + scrollContainer.clientHeight;

						const cardMarginBottom = parseFloat(cardRef.current.style.marginBottom);
						// 16px above the card
						const cardTop = cardRef.current.offsetTop - cardMarginBottom + offset;
						// 16px below the card
						const cardBottom = cardTop + cardRef.current.clientHeight + (cardMarginBottom * 2);

						// if card is outside the visible area, scroll to it
						const above = cardTop < containerTop;
						const below = cardBottom > containerBottom;
						if (above || below) {
							scrollContainer.scrollTo({
								top: above ? cardTop : cardBottom - scrollContainer.clientHeight,
								behavior: 'smooth',
							});
						}
					}
				}
			}}
			ref={cardRef}
		>
			{!openDestinationCard && (
				<div className="destination-header">
					<div className="destination-label">
						<img src={logoImage} alt={logoImage} />
						<span className="bl-font-s1">{name}</span>
					</div>
					<div className="destination-controls">
						{openDestinationCard ? (
							<button className="x-button" onClick={() => handleEditDestination('')}>
								<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={13.33} />
							</button>
						) : (
							<>
								<button onClick={() => { if (!newDestination) handleEditDestination(id); }} className='margin-right-small'>
									<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={13.33} />
								</button>
								<Switch
									on={
										(selected === id && !useDefaultEndpoint && !newDestination)
										|| (isDefaultDestination && useDefaultEndpoint)
									}
									onChange={() => { if (!newDestination) toggleOn(id); }}
									className={'green'}
								/>
							</>
						)}
					</div>
				</div>
			)}
			{openDestinationCard && (
				<div className="destination-card-edit">
					<div className="destination-card-edit-row">
						<img src={logoImage} alt={logoImage} />
						<input
							type="text"
							onChange={({ target }) => setEditName(target.value)}
							value={editName}
							placeholder="Name your destination..."
							style={{ marginRight: '8px' }}
							disabled={isDefaultDestination}
						/>
						<SelectSmall
							options={RESOLUTION_OPTIONS}
							onChange={setEditResolution}
							label={editResolution}
							selected={
								RESOLUTION_OPTIONS.find((option) => option.value === editResolution)
								|| RESOLUTION_OPTIONS[0]
							}
							optionHeight={32}
							dropdown={true}
							className='destination-dropdown bl-font-p2'
						/>
					</div>
					<div className="destination-card-edit-row">
						<span className="bl-font-p2 grayscale-300">RTMP Address:</span>
						<input
							type="text"
							className="rtmp-input"
							onChange={({ target }) => setEditAddress(target.value)}
							value={editAddress}
							placeholder="Paste RTMP address here..."
							disabled={isDefaultDestination}
						/>
					</div>
					<div className="destination-card-edit-row">
						<span className="bl-font-p2 grayscale-300">Stream Key:</span>
						<input
							type="text"
							className="rtmp-input"
							onChange={({ target }) => setEditKey(target.value)}
							value={editKey}
							placeholder="Paste stream key here..."
							disabled={isDefaultDestination}
						/>
					</div>
					<div className="destination-card-edit-row">
						<button
							className="flex-center transparent"
							onClick={() => { setCardHeight(CARD_HEIGHTS.HIDE); }}
							disabled={isDefaultDestination}
						>
							<Icon name={ICONS.MEDIA_DELETE} size={16} color={COLORS.RED} />
							<h5 className="bl-font-l1 semantic-red-400 padding-left-small margin-none">Delete Destination</h5>
						</button>
						<div className="flex-center">
							<button
								className="padding-button-small background-lighter border-radius-100 margin-right-small"
								disabled={false}
								onClick={cancelClick}
							>
								<label className="bl-font-l2 grayscale-200 pointer-events-none">Cancel</label>
							</button>
							<button
								className="padding-button-small background-green border-radius-100 transition-opacity"
								disabled={isValidatingRtmpEndpoint}
								onClick={() => validateRtmpEndpoint(
									editName,
									editAddress,
									editKey,
									3,
									isDefaultDestination
										? saveDefaultResolution
										: saveDestination,
								)}
							>
								{isValidatingRtmpEndpoint && (
									<>
										<LoadingSpinner
											height={10}
											width={10}
											color="#09090A"
											stroke={10}
											style={{ display: 'flex', margin: 'none' }}
										/>
										&nbsp;
									</>
								)}
								<label className="bl-font-l2 grayscale-1200 pointer-events-none">Save</label>
							</button>
						</div>
						{rtmpOverrideModal}
					</div>
				</div>
			)}
		</div>
	);
}
